import { useState } from "react";
import { Link } from "react-router-dom";
import Stage from "@/components/landing/Stage";
import Logomark from "@/components/Logomark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * The public front door while NextOpp is invite-only.
 *
 * Deliberately not an account: nothing is created, nothing is logged into, and
 * the only thing collected is how to reach someone when a place opens.
 *
 * The response is identical whether the address is new, already waiting, or
 * already an account — the server is generic on purpose, and the page must not
 * undo that by rendering three different messages. A public form that says
 * "you already have an account" is a free membership check for any address
 * somebody cares to type.
 */
export default function JoinWaitlist() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/waitlist", { name, email, role, source: "web" });
      setJoined(true);
    } catch (err) {
      // A 400 is the address failing its format check, which the person can
      // fix; anything else is ours and should not be dressed up as their
      // mistake.
      setError(err?.response?.data?.detail
        || "We couldn't add you just now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      <Stage phase={0.15} className="hidden lg:block" contentClassName="h-full flex flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2">
          <Logomark />
          <span className="font-heading font-bold tracking-tight text-lg">NextOpp</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter leading-[1] max-w-md">
            Opening <em className="font-display not-italic text-dawn-300">slowly</em>.
          </h2>
          <p className="mt-6 text-white/70 text-sm max-w-md">
            We're letting people in a few at a time so every account gets a
            product that actually works. Leave your address and we'll write when
            a place opens.
          </p>
          <ul className="mt-8 space-y-3 text-white/70 text-sm max-w-md">
            {[
              "Match your resume to any job in seconds",
              "Get missing ATS keywords and clear recommendations",
              "Generate tailored resumes and cover letters",
              "Track every application in one pipeline",
            ].map((t) => <li key={t}>— {t}</li>)}
          </ul>
        </div>
        <div className="u-label text-white/45">Invite-only while we're in early access</div>
      </Stage>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <Logomark />
              <span className="font-heading font-bold tracking-tight text-lg">NextOpp</span>
            </Link>
          </div>

          {joined ? (
            <div data-testid="waitlist-success">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" aria-hidden="true" />
              <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-stone-950">
                You're on the list
              </h1>
              <p className="mt-3 text-sm text-stone-600">
                We'll email you when a place opens. Nothing else to do — there's
                no account to set up yet, and we won't ask for a card.
              </p>
              <Link to="/" className="mt-8 inline-block">
                <Button variant="outline" className="rounded-sm">Back to the homepage</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600 mb-2">EARLY ACCESS</div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-stone-950">
                Join the waiting list
              </h1>
              <p className="mt-2 text-sm text-stone-600">
                NextOpp is invite-only right now. Leave your address and we'll
                write when a place opens — no account, no card.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="waitlist-form">
                <div>
                  <Label htmlFor="wl-email" className="text-sm font-medium text-stone-900 mb-1.5 block">Email</Label>
                  <Input id="wl-email" type="email" required value={email}
                         onChange={(e) => setEmail(e.target.value)}
                         className="rounded-sm" data-testid="waitlist-email-input" />
                </div>
                <div>
                  <Label htmlFor="wl-name" className="text-sm font-medium text-stone-900 mb-1.5 block">
                    Name <span className="text-stone-500 font-normal">(optional)</span>
                  </Label>
                  <Input id="wl-name" value={name} onChange={(e) => setName(e.target.value)}
                         className="rounded-sm" data-testid="waitlist-name-input" />
                </div>
                <div>
                  <Label htmlFor="wl-role" className="text-sm font-medium text-stone-900 mb-1.5 block">
                    What are you looking for? <span className="text-stone-500 font-normal">(optional)</span>
                  </Label>
                  <Input id="wl-role" value={role} onChange={(e) => setRole(e.target.value)}
                         placeholder="e.g. Senior backend engineer, remote"
                         className="rounded-sm" data-testid="waitlist-role-input" />
                  <div className="text-xs text-stone-600 mt-1">
                    Helps us prioritise who to let in next.
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-rose-700" data-testid="waitlist-error">{error}</p>
                )}

                <Button type="submit" disabled={loading}
                        className="w-full h-11 bg-brand hover:bg-brand-hover text-white rounded-sm font-medium"
                        data-testid="waitlist-submit">
                  {loading ? "Adding you…" : (<>Join the waiting list <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                </Button>
              </form>

              <p className="mt-6 text-sm text-stone-600">
                Already have an account? <Link to="/login" className="underline">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
